import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { googleAuthService, GoogleAuthResponse } from '../services/googleAuth';
import { User, AuthContextData, AuthResponse } from '../types/auth';
import axios from 'axios';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

// Função para decodificar JWT (simulação)
const decodeJWT = (token: string): User | null => {
  try {
    // Em uma implementação real, você usaria uma biblioteca como jwt-decode
    // Por enquanto, vamos simular a decodificação
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.userId || payload.sub || 1,
      email: payload.email || 'user@example.com',
      name: payload.name || 'Usuário',
      picture: payload.picture,
      provider: payload.provider || 'email'
    };
  } catch (error) {
    return null;
  }
};

// Função para verificar se o token está expirado
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Simulação da API de login
const simulateLoginAPI = async (email: string, password: string): Promise<{ access_token: string } | null> => {
  // Simula delay da API
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Credenciais válidas para teste
  if (email === 'admin@test.com' && password === '123456') {
    // Simula um JWT token (na prática viria da API)
    const mockPayload = {
      userId: 1,
      email: email,
      name: 'Administrador',
      provider: 'email',
      exp: Math.floor(Date.now() / 1000) + (60 * 60 * 24) // Expira em 24 horas
    };
    
    const mockToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${btoa(JSON.stringify(mockPayload))}.mock_signature`;
    
    return { access_token: mockToken };
  }
  
  return null;
};

// Função para salvar dados do usuário no localStorage
const saveUserData = (userData: User) => {
  try {
    localStorage.setItem('@ReactorFinances:userData', JSON.stringify(userData));
  } catch (error) {
    // Erro silencioso
  }
};

// Função para recuperar dados do usuário do localStorage
const getUserData = (): User | null => {
  try {
    const userData = localStorage.getItem('@ReactorFinances:userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      return parsedData;
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Função para limpar dados do usuário do localStorage
const clearUserData = () => {
  try {
    localStorage.removeItem('@ReactorFinances:userData');
  } catch (error) {
    // Erro silencioso
  }
};

// Chamada real da API de login com Google
const callGoogleLoginAPI = async (googleResponse: GoogleAuthResponse): Promise<{ access_token: string; user: User } | null> => {
  try {
    // Cria uma instância axios sem interceptadores para esta chamada específica
    const authApiClient = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3334/service/api/v1',
      timeout: 10000,
    });

    const response = await authApiClient.post('/auth/google/callback', {
      access_token: googleResponse.access_token
    });

    // Valida se a resposta tem os campos necessários
    if (!response.data.access_token || !response.data.user) {
      throw new Error('Resposta da API inválida - campos obrigatórios ausentes');
    }

    return {
      access_token: response.data.access_token,
      user: response.data.user
    };

  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Token não autorizado ou expirado');
      } else if (error.response?.status === 500) {
        throw new Error('Erro interno do servidor');
      } else if (error.code === 'ECONNREFUSED') {
        throw new Error('Erro de conexão - servidor indisponível');
      } else {
        throw new Error(`Erro na API: ${error.response?.status || 'Desconhecido'} ${error.response?.statusText || ''}`);
      }
    }
    
    throw error;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verifica se há token salvo no localStorage ao inicializar e inicializa Google Auth
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Inicializa o Google Auth
        await googleAuthService.initialize();
      } catch (error) {
        // Erro silencioso
      }

      const savedToken = localStorage.getItem('@ReactorFinances:token');
      const savedUserData = getUserData();
      
      if (savedToken) {
        if (isTokenExpired(savedToken)) {
          // Token expirado, remove do localStorage
          localStorage.removeItem('@ReactorFinances:token');
          clearUserData();
          setIsLoading(false);
        } else {
          // Token válido, usa dados salvos ou decodifica do JWT
          let userData = savedUserData;
          
          if (!userData) {
            // Se não há dados salvos, decodifica do JWT
            userData = decodeJWT(savedToken);
          }
          
          if (userData) {
            setToken(savedToken);
            setUser(userData);
            
            // Garante que os dados estão salvos no localStorage
            if (!savedUserData) {
              saveUserData(userData);
            }
          }
          setIsLoading(false);
        }
      } else {
        // Se não há token mas há dados do usuário, limpa tudo
        if (savedUserData) {
          clearUserData();
        }
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      
      const response = await simulateLoginAPI(email, password);
      
      if (response && response.access_token) {
        const decodedUser = decodeJWT(response.access_token);
        
        if (decodedUser) {
          // Salva no localStorage primeiro
          localStorage.setItem('@ReactorFinances:token', response.access_token);
          
          // Depois atualiza os estados
          setToken(response.access_token);
          setUser(decodedUser);
          setIsLoading(false);
          
          return { success: true };
        }
      }
      
      setIsLoading(false);
      return { success: false, message: 'Usuário ou senha incorretos' };
    } catch (error) {
      setIsLoading(false);
      return { success: false, message: 'Erro interno do servidor' };
    }
  };

  const loginWithGoogle = async (): Promise<AuthResponse> => {
    try {
      setIsLoading(true);
      
      const googleResponse = await googleAuthService.signIn();
      
      // Chama a API real do backend
      const apiResponse = await callGoogleLoginAPI(googleResponse);
      
      if (apiResponse && apiResponse.access_token && apiResponse.user) {
        // Usa os dados retornados pela API (já processados pelo backend)
        const userData = apiResponse.user;
        
        // Salva token e dados do usuário no localStorage
        localStorage.setItem('@ReactorFinances:token', apiResponse.access_token);
        saveUserData(userData);
        
        // Atualiza os estados
        setToken(apiResponse.access_token);
        setUser(userData);
        setIsLoading(false);
        
        return { success: true };
      }
      
      setIsLoading(false);
      return { success: false, message: 'Erro ao processar resposta da API' };
    } catch (error) {
      setIsLoading(false);
      
      // Mensagens de erro mais específicas
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          return { success: false, message: 'Token não autorizado. Tente fazer login novamente.' };
        } else if (error.message.includes('500')) {
          return { success: false, message: 'Erro no servidor. Tente novamente mais tarde.' };
        } else if (error.message.includes('fetch')) {
          return { success: false, message: 'Erro de conexão. Verifique sua internet.' };
        }
      }
      
      return { success: false, message: 'Erro ao fazer login com Google' };
    }
  };

  const logout = async () => {
    try {
      // Faz logout do Google se o usuário logou com Google
      if (user?.provider === 'google') {
        await googleAuthService.signOut();
      }
    } catch (error) {
      // Erro silencioso
    }
    
    setToken(null);
    setUser(null);
    localStorage.removeItem('@ReactorFinances:token');
    clearUserData();
  };

  const value: AuthContextData = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextData => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};